import { Box, Card, Typography, keyframes } from "@mui/material";
import {
  getColorList,
  getPhoneTypes,
  getStandardViolations,
  getUser,
  getUserTenants,
  getTenants,
} from "api";
import { ACTION_TYPE, AppContext } from "context/appContext";
import { ThemeContext } from "context/themeContext";
import { ReactNode, useContext, useEffect } from "react";
import { useLoaderData, useNavigate } from "react-router";
import { ROUTES, routeWithParams } from "router";
import { PhoneType, Tenant, User, Violation } from "types";
import AuthService from "utils/AuthService";

interface SSOLoaderData {
  user: User;
  userTenants: Tenant[];
  standardViolations: Violation[];
  phoneTypes: PhoneType[];
  colors: string[];
  redirectURL: string;
}

export const ssoLoader = async ({
  request,
}: {
  request: Request;
}): Promise<SSOLoaderData | Response> => {
  const url = new URL(request.url);
  const jwt = url.searchParams.get("jwt");
  const redirectURL = url.searchParams.get("redirectURL") || "";

  if (!jwt) {
    return buildLoginRedirect("Missing authentication token. Please log in.");
  }

  if (AuthService.IsTokenExpired(jwt)) {
    return buildLoginRedirect("Your session has expired. Please log in again.");
  }

  AuthService.SetToken(jwt);

  let user: User;
  try {
    const decoded = AuthService.DecodeJWT(jwt);
    user = await getUser(decoded.id);
    user.jwtToken = jwt;
  } catch {
    AuthService.LogOut();
    return buildLoginRedirect("Unable to verify your identity. Please log in.");
  }

  try {
    const [userTenants, standardViolations, phoneTypes, colors] =
      await Promise.all([
        AuthService.IsSuperAdmin()
          ? getTenants(false)
          : getUserTenants({ userID: user.id, includeDisabled: false }),
        getStandardViolations(),
        getPhoneTypes(),
        getColorList(),
      ]);

    return {
      user,
      userTenants,
      standardViolations,
      phoneTypes,
      colors,
      redirectURL,
    };
  } catch {
    AuthService.LogOut();
    return buildLoginRedirect(
      "Failed to load session data. Please try again.",
    );
  }
};

function buildLoginRedirect(message: string): Response {
  const loginPath = routeWithParams(ROUTES.LOGIN, [""]);
  const params = new URLSearchParams({
    toastMessage: message,
    toastType: "error",
  });
  return Response.redirect(
    `${window.location.origin}${loginPath}?${params.toString()}`,
    302,
  );
}

const getTenantFromURL = (url: string, tenants: Tenant[]): Tenant => {
  let pathComponents = url.split("/");

  try {
    const urlParser = new URL(url);
    pathComponents = urlParser.pathname.split("/");
  } catch {
    return tenants[0];
  }

  if (pathComponents.length > 2 && pathComponents[2]) {
    const foundTenant = tenants.find((t: Tenant) => {
      return pathComponents[2] === t.id.toString();
    });
    if (foundTenant) {
      return foundTenant;
    }
  }

  return tenants[0];
};

const SSO = (): ReactNode => {
  const data = useLoaderData() as SSOLoaderData;
  const navigate = useNavigate();
  const { dispatch } = useContext(AppContext);
  const { setTenantBranding } = useContext(ThemeContext);

  useEffect(() => {
    const {
      user,
      userTenants,
      standardViolations,
      phoneTypes,
      colors,
      redirectURL,
    } = data;

    if (!user || !userTenants || userTenants.length === 0) {
      navigate(routeWithParams(ROUTES.LOGIN, [""]));
      return;
    }

    const tenant = getTenantFromURL(redirectURL, userTenants);

    // set the theme
    setTenantBranding({
      branding: tenant?.data?.branding,
      companyPath: tenant?.data?.companyPath,
    });

    // dispatch all state in the same order as login's setInitialState
    dispatch({ type: ACTION_TYPE.SET_USER, payload: user });  
    dispatch({
      type: ACTION_TYPE.SET_USER_TENANTS,
      payload: userTenants,
    });
    dispatch({
      type: ACTION_TYPE.SET_ACTIVE_TENANT,
      payload: tenant,
    });
    dispatch({ type: ACTION_TYPE.SET_PHONE_TYPES, payload: phoneTypes });
    dispatch({
      type: ACTION_TYPE.SET_STANDARD_VIOLATIONS,
      payload: standardViolations,
    });
    dispatch({ type: ACTION_TYPE.SET_COLORS, payload: colors });
    dispatch({ type: ACTION_TYPE.SET_ACTIVE_TENANT, payload: tenant });
    if (user.signature) {
      dispatch({ type: ACTION_TYPE.SET_SIGNATURE, payload: user.signature });
    }

    // navigate to the redirect URL or fallback to jobs
    if (redirectURL && URL.canParse(redirectURL)) {
      const url = new URL(redirectURL);
      navigate(url.pathname, { replace: true });
    } else {
      navigate(
        routeWithParams(ROUTES.JOBS, [tenant?.id || userTenants[0].id]),
      );
    }
  }, []);

  const pulse = keyframes`
    0%, 100% { opacity: 0; }
    50% { opacity: 1; }
  `;

  return (
    <Box
      sx={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* CRT monitor body */}
      <Box
        sx={{
          backgroundColor: "#c4b69c",
          borderRadius: "18px",
          p: "24px",
          pb: "40px",
          border: "3px solid #a89a80",
          position: "relative",
        }}
      >
        {/* Screen bezel */}
        <Box
          sx={{
            backgroundColor: "#2a2a2a",
            borderRadius: "12px",
            p: "12px",
          }}
        >
          {/* Screen */}
          <Card
            elevation={0}
            sx={{
              width: 420,
              height: 420,
              backgroundColor: "#0a0a0a",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              // CRT scanline overlay
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(0,255,0,0.03) 0px, rgba(0,255,0,0.03) 1px, transparent 1px, transparent 3px)",
              // subtle screen curvature glow
              boxShadow: "inset 0 0 60px rgba(0,255,0,0.05)",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Courier New", Courier, monospace',
                fontSize: "2rem",
                color: "#00ff00",
                textAlign: "center",
                animation: `${pulse} 2s ease-in-out infinite`,
                textShadow: "0 0 8px rgba(0,255,0,0.6)",
              }}
            >
              one moment while we connect the tubes
            </Typography>
          </Card>
        </Box>
        {/* Power indicator */}
        <Box
          sx={{
            position: "absolute",
            bottom: 14,
            right: 32,
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "#00ff00",
            boxShadow: "0 0 4px #00ff00",
          }}
        />
      </Box>
      {/* Monitor stand neck */}
      <Box
        sx={{
          width: 60,
          height: 40,
          backgroundColor: "#a89a80",
          borderLeft: "3px solid #c4b69c",
          borderRight: "3px solid #8a7e68",
        }}
      />
      {/* Monitor stand base */}
      <Box
        sx={{
          width: 180,
          height: 12,
          backgroundColor: "#a89a80",
          borderRadius: "0 0 8px 8px",
          border: "2px solid #8a7e68",
          borderTop: "none",
        }}
      />
    </Box>
  );
};

export default SSO;
